import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import {
  NOT_REGISTERED,
  PENDING,
  REV_IN_PROGRESS,
  APPROBED,
  REJECTED,
  BANNED,
  api_v3_list_of_clans,
} from "../utils/constants";
import { useRegistrationsContext } from "../context/registrations_context";
import { useTournamentData } from "../hooks/useTournamentData";
import RegistrationsItem from "./RegistrationsItem";
import {
  BsFillHandThumbsUpFill,
  BsFillHandThumbsDownFill,
  BsBanFill,
} from "react-icons/bs";
import { TbZoomFilled } from "react-icons/tb";
import { FaUserSlash, FaHourglass } from "react-icons/fa";

const NO_AOE2PROFILE = "NO_AOE2PROFILE";

const FILTERS = [
  { status: NOT_REGISTERED, label: "No registrados", icon: <FaUserSlash />, color: "#3860be" },
  { status: PENDING, label: "Pendientes", icon: <FaHourglass />, color: "#ff9f43" },
  { status: REV_IN_PROGRESS, label: "En revisión", icon: <TbZoomFilled />, color: "#3860be" },
  { status: APPROBED, label: "Aprobados", icon: <BsFillHandThumbsUpFill />, color: "var(--v-mint)" },
  { status: REJECTED, label: "Rechazados", icon: <BsFillHandThumbsDownFill />, color: "var(--v-uv)" },
  { status: BANNED, label: "Baneados", icon: <BsBanFill />, color: "#c9a04e" },
  { status: NO_AOE2PROFILE, label: "Sin perfil aoe2net", icon: null, color: "#ff3b3b" },
];

const RegistrationsList = ({ tournamentId }) => {
  const [clans, setClans] = useState([]);
  const [clansInProgress, setClansInProgress] = useState(false);
  const [clansError, setClansError] = useState(false);
  const { loading: loadingTournaments, error: errorTournaments, tournaments } = useTournamentData();

  const loadClans = async () => {
    try {
      setClansInProgress(true);
      setClansError(false);
      const response = await axios.get(api_v3_list_of_clans());
      if (response.status === 200) {
        const clans_list = response.data.sort((a, b) => (a.name > b.name ? 1 : -1));
        setClans(clans_list);
        setClansInProgress(false);
        return true;
      } else {
        setClansInProgress(false);
        setClansError(true);
        return false;
      }
    } catch (error) {
      setClansInProgress(false);
      setClansError(true);
      return false;
    }
  };

  useEffect(() => { loadClans(); }, []);

  const {
    regInProgress, regError, regCompleted,
    registrations, filterRegisters, resetFilterRegisters,
    allRegistrations, updateCurrentTournament, current_tournament,
  } = useRegistrationsContext();

  useEffect(() => {
    if (tournamentId !== current_tournament) {
      updateCurrentTournament(tournamentId);
    }
  }, [tournamentId, current_tournament, updateCurrentTournament]);

  const [filterSelected, setFilterSelected] = useState(PENDING);

  const setFilter = (status) => {
    setFilterSelected(status);
    filterRegisters(status);
  };

  const resetFilter = () => {
    setFilterSelected("");
    resetFilterRegisters();
  };

  const countByStatus = (status) => {
    if (status === NOT_REGISTERED) {
      return allRegistrations.filter(
        (reg) => reg.tournamentHistory.filter((s) => s.tournamentId === tournamentId).length === 0
      ).length;
    } else if (status === NO_AOE2PROFILE) {
      return allRegistrations.filter((reg) => {
        const regExp = /^\d+$/;
        return (
          (reg.aoe2ProfileId === null || !regExp.test(reg.aoe2ProfileId)) &&
          reg.tournamentHistory.filter((s) => s.tournamentId === tournamentId && s.status !== REJECTED).length > 0
        );
      }).length;
    } else {
      return allRegistrations.filter((reg) =>
        reg.tournamentHistory.some((s) => s.tournamentId === tournamentId && s.status === status)
      ).length;
    }
  };

  return (
    <Canvas>
      {regInProgress && <LoadingBar />}
      {regError && <ErrorBanner>Ha ocurrido un error cargando los inscritos</ErrorBanner>}
      <Content>
        <PageTitle>INSCRIPCIONES</PageTitle>

        <HeaderBar>
          <PlayerCount>
            {registrations.length}
            <PlayerCountLabel>JUGADORES</PlayerCountLabel>
          </PlayerCount>
          <FilterRow>
            {FILTERS.map((f) => (
              <FilterPill
                key={f.status}
                $active={filterSelected === f.status}
                $color={f.color}
                onClick={() => setFilter(f.status)}
              >
                {f.icon && <FilterIcon>{f.icon}</FilterIcon>}
                {f.status === NO_AOE2PROFILE ? f.label : countByStatus(f.status)}
              </FilterPill>
            ))}
            <ClearBtn onClick={resetFilter}>✕</ClearBtn>
          </FilterRow>
        </HeaderBar>

        {regCompleted && (
          <ListContainer>
            {registrations.map((reg) => (
              <RegistrationsItem
                key={`reg-${reg.playerId}`}
                registration={reg}
                tournamentId={tournamentId}
                clans={clans}
                clansInProgress={clansInProgress}
                clansError={clansError}
                tournaments={tournaments}
                loadingTournaments={loadingTournaments}
                errorTournaments={errorTournaments}
              />
            ))}
          </ListContainer>
        )}
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`
  background: var(--v-canvas);
  min-height: 100vh;
  padding: 4rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1100px;
`;

const PageTitle = styled.h1`
  font-family: Anton, sans-serif;
  font-size: clamp(48px, 8vw, 72px);
  font-weight: 400;
  text-transform: uppercase;
  line-height: 1;
  color: var(--v-fg);
  margin: 0 0 2rem;
`;

const LoadingBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--v-mint);
  animation: loadBar 1s ease infinite;
  z-index: 999;

  @keyframes loadBar {
    0% { opacity: 1; }
    50% { opacity: 0.4; }
    100% { opacity: 1; }
  }
`;

const ErrorBanner = styled.div`
  background: rgba(255, 59, 59, 0.1);
  border: 1px solid rgba(255, 59, 59, 0.2);
  color: #ff3b3b;
  font-family: var(--v-font-mono);
  font-size: 11px;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  text-align: center;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const PlayerCount = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-family: var(--v-font-body);
  font-size: 32px;
  font-weight: 700;
  color: var(--v-fg);
  flex-shrink: 0;
`;

const PlayerCountLabel = styled.span`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  color: var(--v-fg-muted);
  letter-spacing: 1px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const FilterPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  border: 1px solid ${({ $active, $color }) => ($active ? $color : "rgba(255,255,255,0.08)")};
  background: ${({ $active, $color }) => ($active ? `${$color}20` : "transparent")};
  color: ${({ $active, $color }) => ($active ? $color : "var(--v-fg-muted)")};
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ $color }) => $color};
    color: ${({ $color }) => $color};
  }
`;

const FilterIcon = styled.span`
  font-size: 11px;
  display: flex;
`;

const ClearBtn = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--v-fg-muted);
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.25);
  }
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export default RegistrationsList;
